
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  DownloadCloud, 
  Edit2, 
  File, 
  FileText, 
  Film, 
  Save, 
  Trash2, 
  X,
  HelpCircle,
  Clock 
} from "lucide-react";
import { FileItem } from "./types";

interface FileTableProps {
  files: FileItem[];
  editingFile: { id: number; name: string } | null;
  newFileName: string;
  onNewFileNameChange: (name: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onStartEdit: (id: number) => void;
  onDeleteFile: (id: number) => void;
  getCategoryLabel: (category: string) => string;
}

export function FileTable({
  files,
  editingFile,
  newFileName,
  onNewFileNameChange,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
  onDeleteFile,
  getCategoryLabel
}: FileTableProps) {
  // Helper function to get icon based on file type
  const getFileIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Film className="h-4 w-4" />;
      case 'document':
        return <FileText className="h-4 w-4" />;
      case 'qa':
        return <HelpCircle className="h-4 w-4" />;
      default:
        return <File className="h-4 w-4" />;
    }
  };

  // Helper function to get color based on category
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'documents':
        return 'bg-blue-500';
      case 'guides':
        return 'bg-green-500';
      case 'webinars':
        return 'bg-yellow-500';
      case 'videos':
        return 'bg-purple-500';
      case 'qa':
        return 'bg-pink-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div>
      {files.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Δεν υπάρχουν αρχεία. Ανεβάστε το πρώτο σας αρχείο χρησιμοποιώντας τη φόρμα.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead>Όνομα</TableHead>
                <TableHead>Κατηγορία</TableHead>
                <TableHead>Μέγεθος</TableHead>
                <TableHead>Ημ/νία</TableHead>
                <TableHead className="text-right">Ενέργειες</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.map((file) => (
                <TableRow key={file.id}>
                  <TableCell>{getFileIcon(file.type)}</TableCell>
                  
                  <TableCell className="font-medium">
                    {editingFile && editingFile.id === file.id ? (
                      <Input 
                        value={newFileName}
                        onChange={(e) => onNewFileNameChange(e.target.value)}
                        className="py-1 h-8"
                      />
                    ) : (
                      <div>
                        {file.title ? (
                          <>
                            <div className="font-semibold">{file.title}</div>
                            <div className="text-sm text-muted-foreground">{file.name}</div>
                          </>
                        ) : (
                          file.name
                        )}

                        {file.description && (
                          <div className="text-sm text-muted-foreground mt-1">
                            {file.description.length > 100 
                              ? `${file.description.substring(0, 100)}...` 
                              : file.description}
                          </div>
                        )}

                        {file.duration && (
                          <div className="flex items-center text-xs text-muted-foreground mt-1">
                            <Clock className="h-3 w-3 mr-1" /> {file.duration}
                          </div>
                        )}
                        
                        {file.type === 'qa' && file.question && (
                          <div className="mt-1">
                            <div className="text-sm text-muted-foreground font-normal">
                              <span className="font-medium">Q:</span> {file.question}
                            </div>
                            <div className="text-sm text-muted-foreground font-normal mt-1">
                              <span className="font-medium">A:</span> {file.answer}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    <Badge variant="secondary" className={`${getCategoryColor(file.category)} text-white`}>
                      {getCategoryLabel(file.category)}
                    </Badge>
                  </TableCell>
                  
                  <TableCell>{file.size}</TableCell>
                  
                  <TableCell>{file.uploadDate}</TableCell>
                  
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {editingFile && editingFile.id === file.id ? (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={onSaveEdit}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={onCancelEdit}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          {file.type !== 'qa' && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              asChild
                            >
                              <a href={file.url} download={file.name} target="_blank" rel="noopener noreferrer">
                                <DownloadCloud className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                          
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => onStartEdit(file.id)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => onDeleteFile(file.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
